import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { commonGetApiHandler } from "../../api/commonApiCall";
import { GET_API_URLS } from "../../utils/apiConstant";
import styles from "./style.module.scss";
import { Col, Container, Row } from "react-bootstrap";
import { SectionTitle, Text } from "../../components/styledComponent";
import { AllLocationsLoadMoreTable, AllLocationsMap } from "../../components/parts";
import { Loader } from "../../components/ui";
import CancoMarker from "../../assets/cancoMarker.png";

const CancoAllLocations = () => {
  const [allLocations, setAllLocations] = useState(null);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    async function fetchLocations() {
      const res = await commonGetApiHandler(GET_API_URLS.GET_LOCATION);
      if (axios.isAxiosError(res) || !Array.isArray(res?.data)) {
        setFetchError(res || true);
        setAllLocations(null);
        return;
      }
      setAllLocations(res.data);
      setFetchError(null);
    }
    fetchLocations();
  }, []);

  const rowsSorted = useMemo(() => {
    if (!allLocations?.length) return null;
    return [...allLocations]
      .map((data) => ({
        store_name: data.name,
        address: data.address,
        amenities: data.amenities,
        lat: data.latitude,
        lng: data.longitude,
        timings: data.timings,
        is24by7: data.is24by7,
        gas_types: data.gas_types,
      }))
      .sort((a, b) =>
        (a.store_name || "").localeCompare(b.store_name || "", undefined, {
          sensitivity: "base",
        })
      );
  }, [allLocations]);

  const listRows = useMemo(() => {
    if (fetchError) return null;
    if (allLocations === null) return null;
    if (!Array.isArray(allLocations)) return null;
    if (allLocations.length === 0) return [];
    return rowsSorted ?? [];
  }, [allLocations, fetchError, rowsSorted]);

  const mapCoordinates = useMemo(() => {
    if (!allLocations?.length) return [];
    return allLocations
      .map((data) => ({
        lat: parseFloat(data.latitude),
        lng: parseFloat(data.longitude),
        name: data.name,
        id: data.id,
        address: data.address,
        city: data.city,
        province: data.province,
      }))
      .filter((d) => Number.isFinite(d.lat) && Number.isFinite(d.lng));
  }, [allLocations]);

  const hasStores = Array.isArray(allLocations) && allLocations.length > 0;
  const mapReady = hasStores && mapCoordinates.length > 0;

  return (
    <div className={styles.cancoAllLocationsPage}>
      <Container className="sectionContainer">
        <Row>
          <Col xs={12}>
            <div className={styles.sectionImg}>
              <img src={CancoMarker} alt="Canco map marker" />
            </div>
            <SectionTitle $textAlign="center">
              Canco all locations
            </SectionTitle>
            <div className={styles.secDescriptionBlock}>
              <Text $textAlign="center">
                Every Canco site on one map, with a full list below. Browse all
                stations and open a location for details or directions.
              </Text>
            </div>
          </Col>
          <Col xs={12} className={styles.mapSection}>
            {fetchError ? (
              <div className={styles.mapError}>
                <Text>There was an error while fetching locations.</Text>
              </div>
            ) : allLocations === null ? (
              <div className={styles.mapLoaderContainer}>
                <Loader />
              </div>
            ) : !hasStores ? (
              <div className={styles.mapError}>
                <Text>No locations are available at this time.</Text>
              </div>
            ) : !mapReady ? (
              <div className={styles.mapError}>
                <Text>No valid map coordinates for these locations.</Text>
              </div>
            ) : (
              <AllLocationsMap coordinates={mapCoordinates} />
            )}
            <div className={styles.locationTable}>
              <AllLocationsLoadMoreTable
                hideDistance
                locationData={listRows}
                dataError={fetchError}
              />
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default CancoAllLocations;
